import {testLogging} from '../util';

import {BaseService} from './base';

import {StructuredConsole} from '../util/structured-logging';


function objToURLSearchParams(obj: object): URLSearchParams {
    const params = new URLSearchParams();
    for (const [key, val] of Object.entries(obj)) {
        if (val !== undefined) {
            if (Array.isArray(val)) {
                for (const v of val) {
                    params.append(key, v);
                }
            } else {
                params.append(key, val);
            }
        }
    }
    return params;
}

interface MyRequestInit extends RequestInit {
    url: string;
    params: object;
    headers: Headers;
    tryBeacon?: boolean;
}

export class HttpService extends BaseService {
    logger: StructuredConsole;

    constructor(context: jsx.AppGlobals) {
        super(context, {});
        this.logger = context.request.console.addIdentifier(
            {component: 'httpService'});
    }
    request(o: MyRequestInit): Promise<Response> {
        // https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch
        let url = o.url;
        delete o.url;
        const tryBeacon = o.tryBeacon;
        delete o.tryBeacon;
        // https://developer.mozilla.org/en-US/docs/Web/API/RequestInit#priority
        // Set priority for fetch requests to high so that API requests are
        // prioritized over images and other resources.
        // fetch requests should already get prioritized over images, but in
        // actual testing this seemed to make a difference, and as long as it's
        // not overused it shouldn't hurt.
        // We'll only set it on non-beacon requests, since those are guaranteed
        // to get sent by the browser and do not return a usable response.
        if (!tryBeacon) {
            o.priority = 'high';
        }

        const start = Date.now();
        if (o.body && typeof o.body !== 'string' &&
            o.headers.get('content-type') === 'application/x-www-form-urlencoded') {
            // Need toString() for Safari support, which doens't handle our
            // URLSearchParams polyfill ("unsupported BodyInit type" shows in
            // the console).
            o.body = objToURLSearchParams(o.body).toString();
        }
        url = encodeURI(url);
        if (o.params && (!o.body || ['PATCH'].includes(o.method))) {
            // WARNING: assumes no query already on URL. Done this way because
            // `new URL()` isn't supported in node.
            url += `?${objToURLSearchParams(o.params)}`;
            delete o.params;
        }
        // TODO: need to fix headers?
        o.mode = o.mode || 'cors';
        testLogging('fetchStarted');
        if (global.AbortSignal && AbortSignal.timeout) {
            // Abort requests after 3 seconds on the server, after 60 seconds
            // in the browser to accommodate users on very slow connections.
            o.signal = AbortSignal.timeout(process.env.BROWSER ? 60000 : 3000);
        } else {
            this.logger.warn('AbortSignal.timeout not available.');
        }
        let promise;
        if (tryBeacon) {
            // If sending the beacon fails and we fall back to fetch, we should
            // use keepalive to make sure it goes through even if the window
            // closes.
            o.keepalive = true;

            if (!window.navigator.sendBeacon) {
                this.logger.warn('sendBeacon not supported.');
            } else if (!process.env.BROWSER || o.method !== 'POST' || o.body) {
                this.logger.warn(
                    'Can only use beacon in a browser for POST requests with no body.');
            } else {
                const beaconSent = window.navigator.sendBeacon(url);
                if (beaconSent) {
                    // Beacons don't provide a Response and are only used when
                    // we don't care about the response data, but the rest of
                    // this function expects a Response to be resolved, so use
                    // a stub.
                    promise = Promise.resolve(new Response());
                }
            }
        }
        if (!promise) {
            // A Beacon was not used, so use fetch.
            promise = fetch(url, o);
        }

        // Strictly speaking, requestSize and responseSize are supposed to
        // include the header size.
        const httpRequestLogEntry = {
            requestMethod: o.method,
            requestUrl: url,
            requestSize: (o.body ?
                o.body.toString().length : undefined)?.toString(),
        };
        return promise.then((response) => {
            this.logger.httpDone({
                ...httpRequestLogEntry,
                status: response.status,
                responseSize: response.headers.get('content-length')?.toString(),
                latency: `${(Date.now() - start) / 1000}s`,
            }, {
                requestHeaders: Object.fromEntries(o.headers),
                responseHeaders: response.headers,
            });
            testLogging('fetchFinished');
            return response;
        }).catch((err) => {
            this.logger.httpError({
                ...httpRequestLogEntry,
                latency: `${(Date.now() - start) / 1000}s`,
            }, {
                requestHeaders: Object.fromEntries(o.headers),
            }, err.message);
            testLogging('fetchErrored');
            return Promise.reject(err);
        });
    }
}
